/**
 * /review/plans/new  — create a new plan
 * /review/plans/:id/edit — edit an existing user plan
 *
 * Three-step scrollable form: Focus → How → Name and save.
 */

import { type ReactNode, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  MVP_NODES,
  APP_PLANS,
  type SavedPlan,
} from "@music-app/learning-system";
import { usePrefsStore, useSavedPlansStore } from "@music-app/persistence";
import { buttonClasses } from "../components/Button.js";
import { PageHeader } from "../components/PageHeader.js";

type TrackId = string;

const TRACK_LABELS: Record<string, string> = {
  pitch: "Pitch, Rhythm & Degrees",
  intervals: "Intervals",
  harmony: "Harmony",
  modes: "Modes",
  dictation: "Dictation",
  guitar: "Guitar",
};

const ALL_TRACKS = [...new Set(MVP_NODES.map((n) => n.track))];

export function PlanBuilderPage(): ReactNode {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams<{ id?: string }>();
  const duplicateFromId = searchParams.get("duplicateFrom");

  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);
  const setActiveSavedPlanId = usePrefsStore((s) => s.setActiveSavedPlanId);
  const activeSavedPlanId = usePrefsStore((s) => s.activeSavedPlanId);
  const userPlans = useSavedPlansStore((s) => s.plans);
  const savePlanStore = useSavedPlansStore((s) => s.savePlan);

  const sourcePlan: SavedPlan | undefined = useMemo(() => {
    if (editId) return userPlans.find((p) => p.id === editId);
    if (duplicateFromId) return APP_PLANS.find((p) => p.id === duplicateFromId) ?? userPlans.find((p) => p.id === duplicateFromId);
    return undefined;
  }, [editId, duplicateFromId, userPlans]);

  const isEdit = Boolean(editId);
  const isDuplicate = Boolean(duplicateFromId);

  // ── Step 1: focus tracks ──────────────────────────────────────────────────
  const [allTracks, setAllTracks] = useState<boolean>(() => sourcePlan?.focusTracks === null);
  const [selectedTracks, setSelectedTracks] = useState<Set<TrackId>>(() => {
    if (sourcePlan?.focusTracks) return new Set(sourcePlan.focusTracks);
    return new Set();
  });

  const visibleTracks = useMemo(
    () => ALL_TRACKS.filter((t) => t !== "guitar" || enabledInstrumentTracks.includes("guitar")),
    [enabledInstrumentTracks],
  );

  const toggleAllTracks = useCallback(() => {
    setAllTracks(true);
    setSelectedTracks(new Set());
  }, []);

  const toggleTrack = useCallback((track: TrackId) => {
    setAllTracks(false);
    setSelectedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(track)) { next.delete(track); } else { next.add(track); }
      return next;
    });
  }, []);

  // ── Step 2: mix ───────────────────────────────────────────────────────────
  const [sessionMix, setSessionMix] = useState<SavedPlan["sessionMix"]>(
    sourcePlan?.sessionMix ?? "balanced",
  );

  // ── Step 3: name ──────────────────────────────────────────────────────────
  const [name, setName] = useState<string>(() => {
    if (sourcePlan && isDuplicate) return `${sourcePlan.name} (copy)`;
    if (sourcePlan) return sourcePlan.name;
    return "";
  });

  const effectiveFocusTracks: string[] | null = allTracks ? null : [...selectedTracks];

  const isValid = name.trim().length > 0;

  const handleSave = useCallback(
    async (setActive: boolean) => {
      if (!isValid) return;

      const now = new Date().toISOString();
      const plan: SavedPlan & { updatedAt: string; createdAt: string } = {
        id: isEdit ? editId! : `user-${crypto.randomUUID()}`,
        name: name.trim(),
        source: "user",
        generatorId: "fsrs-standard",
        sessionMix,
        focusTracks: effectiveFocusTracks,
        focusNodeIds: null,
        configOverrides: null,
        generatorState: null,
        createdAt: (isEdit ? sourcePlan?.createdAt : undefined) ?? now,
        updatedAt: now,
      };

      await savePlanStore(plan);
      if (setActive) {
        await setActiveSavedPlanId(plan.id);
      }
      void navigate("/review");
    },
    [isValid, isEdit, editId, name, sessionMix, effectiveFocusTracks, sourcePlan, savePlanStore, setActiveSavedPlanId, navigate],
  );

  const warningTracks = useMemo(() => {
    if (allTracks) return [];
    return [...selectedTracks].filter((t) => !MVP_NODES.some((n) => n.track === t));
  }, [allTracks, selectedTracks]);

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="Practice"
        title={isEdit ? "Edit plan" : "New plan"}
      />

      {/* ── Step 1: Focus ── */}
      <section className="mb-8" aria-labelledby="step1-heading">
        <h2 id="step1-heading" className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Step 1 — What do you want to work on?
        </h2>
        <div className="flex flex-wrap gap-2">
          <TrackChip
            label="All tracks"
            active={allTracks}
            onClick={toggleAllTracks}
          />
          {visibleTracks.map((track) => (
            <TrackChip
              key={track}
              label={TRACK_LABELS[track] ?? track}
              active={!allTracks && selectedTracks.has(track)}
              onClick={() => toggleTrack(track)}
            />
          ))}
        </div>
        {!allTracks && selectedTracks.size === 0 && (
          <p className="mt-2 font-serif italic text-xs text-inkFaded">
            Select at least one track, or choose All tracks.
          </p>
        )}
        {warningTracks.length > 0 && (
          <p className="mt-2 font-serif italic text-xs text-inkSoft">
            You haven&apos;t started any{" "}
            {warningTracks.map((t) => TRACK_LABELS[t] ?? t).join(", ")}{" "}
            nodes yet — this plan will have nothing to practice until you do.
          </p>
        )}
      </section>

      {/* ── Step 2: How ── */}
      <section className="mb-8" aria-labelledby="step2-heading">
        <h2 id="step2-heading" className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Step 2 — Practice style and mix
        </h2>

        <fieldset className="mb-4">
          <legend className="font-mono text-[10px] text-inkFaded mb-1.5">Practice style</legend>
          <label className="flex items-start gap-2.5 mb-1.5 cursor-pointer">
            <input type="radio" name="algorithm" value="fsrs" checked readOnly className="mt-0.5" />
            <span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Spaced repetition (FSRS)</span>
              <span className="block font-serif italic text-[13px] text-inkSoft">Recommended — schedules reviews based on memory science.</span>
            </span>
          </label>
          <label className="flex items-start gap-2.5 opacity-40 cursor-not-allowed">
            <input type="radio" name="algorithm" value="drill" disabled className="mt-0.5" />
            <span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Drill-only</span>
              <span className="block font-serif italic text-[13px] text-inkSoft">Coming later — pure repetition without spaced scheduling.</span>
            </span>
          </label>
        </fieldset>

        <fieldset>
          <legend className="font-mono text-[10px] text-inkFaded mb-1.5">Mix</legend>
          {(["review-only", "balanced", "new-focus"] as const).map((mix) => (
            <label key={mix} className="flex items-start gap-2.5 mb-1.5 cursor-pointer">
              <input
                type="radio"
                name="mix"
                value={mix}
                checked={sessionMix === mix}
                onChange={() => setSessionMix(mix)}
                className="mt-0.5"
              />
              <span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{MIX_LABELS[mix]}</span>
                <span className="block font-serif italic text-[13px] text-inkSoft">{MIX_DESCRIPTIONS[mix]}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </section>

      {/* ── Step 3: Name ── */}
      <section className="mb-8" aria-labelledby="step3-heading">
        <h2 id="step3-heading" className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Step 3 — Name and save
        </h2>
        <label className="block mb-1 font-mono text-[10px] text-inkFaded" htmlFor="plan-name">
          Plan name
        </label>
        <input
          id="plan-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Scale sprint"
          maxLength={80}
          className="w-full border border-ink/20 bg-paper px-3 py-2 font-sans text-sm focus:outline-none focus:border-ink/40 mb-4"
        />
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void handleSave(activeSavedPlanId !== (isEdit ? editId : undefined))}
            disabled={!isValid || (!allTracks && selectedTracks.size === 0)}
            className={buttonClasses("primary") + " disabled:opacity-40 disabled:cursor-not-allowed"}
          >
            {isEdit ? "Save changes" : "Save plan"}
          </button>
          {!isEdit && (
            <button
              type="button"
              onClick={() => void handleSave(true)}
              disabled={!isValid || (!allTracks && selectedTracks.size === 0)}
              className={buttonClasses("secondary") + " disabled:opacity-40 disabled:cursor-not-allowed"}
            >
              Save and set active
            </button>
          )}
          <button
            type="button"
            onClick={() => void navigate("/review")}
            className={buttonClasses("text")}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const MIX_LABELS: Record<SavedPlan["sessionMix"], string> = {
  "review-only": "Review heavy",
  "balanced": "Balanced",
  "new-focus": "New material",
};

const MIX_DESCRIPTIONS: Record<SavedPlan["sessionMix"], string> = {
  "review-only": "Mostly due items, little new material.",
  "balanced": "Default — due items and a bit of new content.",
  "new-focus": "Push forward faster with more new material.",
};

function TrackChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`font-mono text-[10px] px-3 py-1.5 transition-colors ${
        active
          ? "bg-ink text-paper"
          : "border border-ink/20 text-inkSoft hover:border-ink/40"
      }`}
    >
      {label}
    </button>
  );
}
